/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import type {
    GetStaticPropsContext,
    InferGetStaticPropsType,
    NextPage,
    PreviewData,
} from "next";
import { ParsedUrlQuery } from "querystring";

import { ColumnGaps, Spacing } from "@/tokens/spacing";

import { Divider } from "@/ui/atoms/divider";
import { GridColumn } from "@/ui/atoms/grid_column";
import { GridContainer } from "@/ui/atoms/grid_container";
import { Text } from "@/ui/atoms/text";
import { ContactItem } from "@/ui/molecules/contact_item";

import { generatePageLevelProps } from "@/util/data_util";
import { generateLegacyPreFooterData } from "@/util/strapi_data_util";
import { getContactPage, getGlobals } from "@/util/strapi_util";
import { buildStylesByBreakpoint } from "@/util/style_util";

export const getStaticProps = async ({
    preview: previewMode = false,
}: GetStaticPropsContext<ParsedUrlQuery, PreviewData>) => {
    const slug = "contact";

    const globalsData = await getGlobals(previewMode);
    const pageData = await getContactPage(previewMode);

    return {
        props: {
            ...generatePageLevelProps({ path: `/${slug}`, previewMode, slug }),
            ...generateLegacyPreFooterData(globalsData, pageData.Pre_Footer),
            globalsData,
            pageData,
        },
    };
};

type PageProps = InferGetStaticPropsType<typeof getStaticProps>;

const Page: NextPage<PageProps> = (props) => {
    /**
     * Styles
     */
    const contactItemsContainerStyles = css(
        {
            display: "flex",
        },
        buildStylesByBreakpoint("flexDirection", {
            extraSmall: "column",
            medium: "row",
        }),
        buildStylesByBreakpoint("gap", {
            ...ColumnGaps,
            extraSmall: Spacing["spacing-0"],
            small: Spacing["spacing-0"],
        }),
    );

    const contactItemStyles = css(
        {
            display: "flex",
        },
        buildStylesByBreakpoint("flexDirection", {
            extraSmall: "column",
            medium: "row",
        }),
        buildStylesByBreakpoint("gap", {
            extraSmall: Spacing["spacing-0"],
            medium: Spacing["spacing-8"],
        }),
    );

    /**
     * Rendering
     */
    return (
        <GridContainer>
            <Text
                columnSpan="full"
                columnStart={1}
                fontSize="HeadlineDefault"
                marginBottom="ContactPageHeadlineMarginBottom"
                marginTop="ContactPageHeadlineMarginTop"
                tag="h1"
                textAlign={{ extraSmall: "left", medium: "center" }}
            >
                {props.pageData.Headline}
            </Text>

            {props.pageData.Contact_Items && (
                <GridColumn
                    className={contactItemsContainerStyles}
                    columnSpan={{
                        extraSmall: 6,
                        medium: 10,
                        large: 8,
                    }}
                    columnStart={{
                        extraSmall: 1,
                        medium: 2,
                        large: 3,
                    }}
                >
                    {props.pageData.Contact_Items.map((_contactItem) => (
                        <div
                            css={contactItemStyles}
                            key={`contact-page::contact-item::${_contactItem.id}`}
                        >
                            <Divider
                                orientation={{
                                    extraSmall: "horizontal",
                                    medium: "vertical",
                                }}
                            />

                            <ContactItem {..._contactItem} />
                        </div>
                    ))}
                </GridColumn>
            )}
        </GridContainer>
    );
};

export default Page;
